import React, { useMemo } from "react";
import { useSnackbar } from "notistack";
import { Backdrop, Box, Button, MenuItem } from "@mui/material";
import { Field, Form, Formik } from "formik";
import { Select } from "formik-mui";

import { axios } from "../api";
import { CancelButton } from "../components/Buttons";
import LoadingSpinner from "../components/Loading";
import Sheet, { SheetContent, SheetHeader } from "../components/Sheet";
import { InvoiceList } from "./types";

interface InvoiceStatusUpdateProps {
  open: boolean;
  onClose: () => void;
  invoices: InvoiceList[];
  config: { [key: string]: any };
  onUpdated?: () => void;
}

const InvoiceStatusUpdate: React.FC<InvoiceStatusUpdateProps> = (props) => {
  const { open, onClose, invoices, config, onUpdated } = props;
  const { enqueueSnackbar } = useSnackbar();

  const initialStatus = useMemo(() => {
    if (!invoices || invoices.length === 0) return "";
    const first = invoices[0]?.status_id ?? "";
    const allSame = invoices.every(
      (invoice) => String(invoice?.status_id ?? "") === String(first)
    );
    return allSame ? String(first) : "";
  }, [invoices]);

  const handleSubmit = async (values: { status_id: string }) => {
    try {
      if (!invoices || invoices.length === 0) return;

      const updatePromises = invoices.map((invoice) =>
        axios
          .put(`/v6/invoice/${invoice.id}`, { status_id: values.status_id })
          .then(() => ({ id: invoice.id, ok: true }))
          .catch((error) => ({ id: invoice.id, ok: false, error }))
      );

      const results = await Promise.allSettled(updatePromises);
      const resolved = results
        .filter(
          (
            result
          ): result is PromiseFulfilledResult<{
            id: number;
            ok: boolean;
            error?: any;
          }> => result.status === "fulfilled"
        )
        .map((result) => result.value);

      const fulfilledCount = resolved.filter((result) => result.ok).length;
      const rejected = resolved.filter((result) => !result.ok);

      if (fulfilledCount > 0) {
        enqueueSnackbar(
          `Status updated successfully for ${fulfilledCount} invoice(s)`,
          {
            variant: "success",
            anchorOrigin: { vertical: "top", horizontal: "right" },
          }
        );
      }

      if (rejected.length > 0) {
        enqueueSnackbar(`Failed to update status for ${rejected.length} invoice(s)`, {
          variant: "warning",
          anchorOrigin: { vertical: "top", horizontal: "right" },
        });
        // eslint-disable-next-line no-console
        console.error("Invoice status update failures: ", rejected);
      }

      if (onUpdated) {
        onUpdated();
      }
      onClose();
    } catch (error) {
      enqueueSnackbar("Error updating invoice status", {
        variant: "error",
        anchorOrigin: { vertical: "top", horizontal: "right" },
      });
      console.error(error);
    }
  };

  if (!config || !invoices || invoices.length === 0) {
    return <LoadingSpinner />;
  }

  return (
    <Sheet open={open} onClose={onClose}>
      <SheetHeader
        onClose={onClose}
        title="Set Status"
        subtitle={invoices.length > 1 ? `${invoices.length} rows selected` : ""}
      />
      <SheetContent>
        <Formik
          initialValues={{ status_id: initialStatus }}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ submitForm, isSubmitting }) => (
            <Form>
              {config.status ? (
                <>
                  <Field
                    component={Select}
                    id="status"
                    name="status_id"
                    label="Status"
                  >
                    {Object.entries(config.status).map(([key, value]) => (
                      <MenuItem value={key} key={key}>
                        {value as string}
                      </MenuItem>
                    ))}
                  </Field>
                  <br />
                </>
              ) : null}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
                <CancelButton onClick={onClose} />
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting}
                  onClick={submitForm}
                >
                  Submit
                </Button>
              </Box>
              <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={isSubmitting}
              >
                <LoadingSpinner />
              </Backdrop>
            </Form>
          )}
        </Formik>
      </SheetContent>
    </Sheet>
  );
};

export default InvoiceStatusUpdate;
